import React, { useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import { ConditionalLoader } from 'dashboard409a/pages/Operations/components';
import { StepLabelProps } from './StepLabel.props';
import { StyledStepLabel } from './StepLabel.styles';
import StepIcon from '../StepIcon/StepIcon';
import { StepStatus } from '../ValuationStatusTracker.types';

const CustomStepIcon = ({ status, isLoading }: { status: StepStatus; isLoading: boolean }) => (
  <StepIcon status={status} isLoading={isLoading} />
);

interface StepIconSlotProps {
  status: StepStatus;
  isLoading: boolean;
  [key: string]: unknown;
}

const StepIconSlot = ({ status, isLoading, ...props }: StepIconSlotProps) => (
  <CustomStepIcon status={status} isLoading={isLoading} {...props} />
);

const StepLabel = ({ label, step, index, isLoading, disabled = false, ...rest }: StepLabelProps) => {
  const theme = useTheme();

  const getStepStyles = () => {
    const baseStyles = {
      padding: theme.spacing(0.75),
      borderRadius: theme.shape.borderRadius * 2,
      fontSize: theme.typography.body2.fontSize,
      display: 'inline-block',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    };

    switch (step) {
      case StepStatus.COMPLETED:
        return {
          ...baseStyles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        };

      case StepStatus.ACTIVE:
        return {
          ...baseStyles,
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.main,
        };

      case StepStatus.PENDING:
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: theme.palette.grey[400],
        };

      default:
        return baseStyles;
    }
  };

  const stepIconSlot = useCallback(
    (props: Record<string, unknown>) => <StepIconSlot status={step} isLoading={isLoading || false} {...props} />,
    [step, isLoading]
  );

  return (
    <StyledStepLabel
      {...rest}
      slots={{ stepIcon: stepIconSlot }}
      onClick={disabled ? undefined : () => rest.onClick?.()}>
      <ConditionalLoader isLoading={isLoading} width={190}>
        <Box style={getStepStyles()}>{label}</Box>
      </ConditionalLoader>
    </StyledStepLabel>
  );
};

export default StepLabel;
